<template>
    <card title="Audit Tracking">
        <EnumSelector v-model="selectedStatus" label="Status" :enum-codes="auditStatus" :clearable="true"
            :display-names="[{}]" />

        <b-table striped :items="items" :fields="fields" :current-page="currentPage" :per-page="perPage" show-empty
            :empty-text="emptyTableText">
            <template #cell(actions)="data">
                <a href="#" @click.prevent="openAuditEditForm(data.item)">
                    <i class="fa fa-edit" aria-hidden="true" v-b-tooltip.hover title="Edit Audit"></i>
                </a>
            </template>
        </b-table>

        <BottomPagerBar v-model="currentPage" :per-page="perPage" :total-rows="items.length" :loading="isLoading"
            show-total show-page-size-selector item-name="audit" @change-page-size="perPage = $event">
            <template #left>
                <b-button variant="success" @click="newAudit">
                    <i class="fa fa-plus"></i>
                    Add Audit
                </b-button>
            </template>
        </BottomPagerBar>

        <b-modal id="newAuditModal" hide-footer size="xl" :close="showComponent('test')" title="New Audit">
            <AuditPageComponent create-new-audit no-close-on-backdrop @close="showComponent('hideAuditForm')">
            </AuditPageComponent>
        </b-modal>

        <b-modal id="editAuditModal" hide-footer size="xl" no-close-on-backdrop title="Edit Audit">
            <AuditPageComponent :au="getEditAudit" :createNewAudit="false" @close="showComponent('hideEditAuditForm')">
            </AuditPageComponent>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import axios, { AxiosRequestConfig } from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Audit, AuditStatus } from '@/models/Audit';
import EnumSelector from "@/components/EnumSelector.vue";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { Watch } from "vue-property-decorator";
import AuditPageComponent from '@/pages/AuditTracking/AuditPage.vue'
import moment from 'moment';
import numeral from "numeral";

@Component({
    name: "AuditTrackingPage",
    components: {
        EnumSelector,
        BottomPagerBar,
        AuditPageComponent
    }
})

export default class AuditTrackingPage extends Vue {
    private isLoading = false;
    private items: Array<Audit> = new Array<Audit>();
    private auditStatus = AuditStatus;
    private selectedStatus: string | null = null;
    private getEditAudit: Audit = new Audit();
    private currentPage: number = 1;
    private perPage: number = 25;
    private fields: any[] = [
        // { key: "id", label: 'ShortageId'},
        { key: "pbm.name", label: "PBM" },
        { key: "auditNumber", label: "Audit ID" },
        { key: "initialNotificationDate", label: "Audit Notification Date", formatter: this.formattedDate },
        { key: "totalExposure", label: "Total Exposure", formatter: this.moneyFormatter },
        { key: "dueDate", label: "Audit Due Date", formatter: this.formattedDate },
        { key: "actions", label: 'Actions' },
    ];

    moneyFormatter(value: number) {
        return numeral(value).format('$0,0.00');
    }

    get hasSelectedStatus() {
        return this.selectedStatus !== null && this.selectedStatus !== undefined;
    }

    get emptyTableText(): string {
        if (!this.hasSelectedStatus) {
            return "Please select a status to view audit tracking records.";
        }
        return "No records found.";
    }

    created() {
        console.log("%c Created AuditTrackingPage", "color: green");
    }

    beforeDestroy() {
        console.log('%c Destroying AuditTrackingPage', 'color: red');
    }

    async loadItems() {
        // Don't make API call if no status is selected
        if (!this.hasSelectedStatus) {
            this.items = [];
            return;
        }

        this.isLoading = true;
        const config: AxiosRequestConfig = { params: { searchTerm: this.selectedStatus } };

        try {
            const res = await axios.get<Array<Audit>>('AuditTracking', config);
            this.items = res.data.map(i => new Audit(i));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoading = false;
        }
    }

    @Watch("selectedStatus")
    selectedStatusUpdate() {
        console.log("fetch the list for the audit status we've now selected.");
        this.currentPage = 1;
        return this.loadItems();
    }

    newAudit() {
        this.$bvModal.show("newAuditModal");
    }

    showComponent(close?: string) {
        switch (close) {
            case 'hideAuditForm':
                this.$bvModal.hide("newAuditModal");
                this.loadItems();
                break;
            case 'hideEditAuditForm':
                this.$bvModal.hide("editAuditModal");
                this.loadItems();
                break;
        }
    }

    formattedDate(value: Date): string {
        const date = moment.utc(value).local();
        return date.format("MM-DD-YYYY");
    }

    openAuditEditForm(au: Audit) {
        this.getEditAudit = au;
        this.$bvModal.show('editAuditModal');
    }
}
</script>